import { getAuthToken as readAuthCredential } from '@syia-ai/utils/auth';
import { HANDOVER_PARAMS, decodeJwtPayload, readHandoverToken } from './handoverParams';
import { reportError } from '@/observability/clientLogger';

const AUTH_TOKEN_KEY = 'authToken';
const TENANT_KEY = 'tenant';

/**
 * Extract the handover JWT from URL query parameters — `?authToken=` or the SPA's
 * `?userToken=` (see utils/handoverParams).
 * @param url - The URL to extract from (can be window.location.search or router.asPath)
 * @returns The auth token if found, null otherwise
 */
export const extractAuthTokenFromUrl = (url: string): string | null => {
  try {
    // Handle both full URLs and query strings
    const searchParams = new URLSearchParams(
      url.includes('?') ? url.split('?')[1] : url
    );

    return readHandoverToken(searchParams);
  } catch (error) {
    reportError(error, { event_detail: 'auth_token_url_parse_failed' });
    return null;
  }
};

/**
 * Write one of the credential-bootstrap cookies.
 *
 * Flags must match the ones middleware.ts sets for the same cookie names. This
 * function also runs on the token middleware just stored — AuthGuard re-writes
 * it on mount to keep localStorage in step — so a laxer write here would quietly
 * strip `Secure`/`SameSite` back off a cookie the server had hardened. The one
 * deliberate divergence is SameSite in a frame; see below for why Lax there is
 * not a stricter cookie but an absent one.
 *
 * Not HttpOnly, and cannot be from JS at all; see the note in middleware.ts for
 * why these two cookies stay script-readable and what would have to move first.
 */
const writeCredentialCookie = (name: string, value: string, maxAgeS: number): void => {
  const https = window.location.protocol === 'https:';
  const secure = https ? '; Secure' : '';

  // Lax everywhere except inside a cross-site frame, where a Lax cookie is simply
  // never sent — which is the whole reason the mobile app's Micro Apps panel could
  // not authenticate a server-rendered page.
  //
  // With no server render left, nothing in this app depends on that any more: every
  // route loads its data through an API call that carries a Bearer header from
  // localStorage, which no cookie policy can drop. The cookie is still written because
  // the SERVER reads it — the Express API routes accept it as a credential alongside the
  // header — and because the handover middleware sets the same pair, so a laxer write
  // here would strip flags off a cookie the server had hardened.
  //
  // None requires Secure, so on http (localhost) it stays Lax rather than being written
  // as a cookie the browser would discard.
  let framed = false;
  try {
    framed = window.top !== window.self;
  } catch {
    // Cross-origin parent: the access threw, which itself means we are framed.
    framed = true;
  }

  const sameSite = framed && https ? 'None' : 'Lax';
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeS}; SameSite=${sameSite}${secure}`;
};

/**
 * Store auth token in localStorage and cookie, extract tenant information
 * @param token - The token to store
 */
export const storeAuthToken = (token: string): void => {
  try {
    if (typeof window !== 'undefined') {
      // Decode first: the token's own `exp` decides the cookie lifetime, and whether
      // it is stored at all. A flat 30 days used to be written regardless, which
      // outlived the credential it carried — an expired JWT kept being sent to the
      // server for weeks after it stopped being usable.
      // decodeJwtPayload, not jsonwebtoken's jwt.decode. This is browser code, and
      // `jsonwebtoken` is a Node library: it reaches for crypto, stream, util and Buffer,
      // none of which exist here. Next hid that by stubbing those builtins out on the
      // client, so the import "worked" — the decode is only base64 and never touched
      // them — while shipping a crippled crypto library to every visitor. decodeJwtPayload
      // is the same base64 decode written for exactly this reason (the Edge middleware
      // could not use jsonwebtoken either) and returns null instead of throwing.
      const decoded = decodeJwtPayload(token);
      if (!decoded) {
        reportError(new Error('Token payload could not be decoded'), {
          event_detail: 'auth_token_tenant_decode_failed',
        });
      }

      // A token with no exp is refused outright rather than given a default lifetime.
      // Inventing one mints a credential store that outlives anything we can vouch for,
      // and auth-service always issues exp — so a token without one is malformed, not
      // merely unusual. Mirrors the same rule in middleware.ts.
      const maxAgeS =
        typeof decoded?.exp === 'number' ? decoded.exp - Math.floor(Date.now() / 1000) : 0;

      if (maxAgeS <= 0) {
        // Refused, so the user stays signed out with a token in hand — the most
        // confusing failure of the set, and invisible without this line.
        reportError(new Error('Token has no usable exp claim'), { event_detail: 'auth_token_refused_no_exp' });
        return;
      }

      // Store the auth token in localStorage and cookie (cookie needed for server-side access)
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      writeCredentialCookie(AUTH_TOKEN_KEY, token, maxAgeS);

      if (decoded?.data?.tenant) {
        const tenant = decoded.data.tenant;
        // Store tenant in localStorage
        localStorage.setItem(TENANT_KEY, tenant);
        // Store tenant in cookie for server-side access
        writeCredentialCookie(TENANT_KEY, tenant, maxAgeS);
      } else {
        reportError(new Error('Token carries no tenant claim'), { event_detail: 'auth_token_no_tenant' });
      }
    }
  } catch (error) {
    reportError(error, { event_detail: 'auth_token_store_failed' });
  }
};

/**
 * Retrieve the auth credential — delegates to the shared @syia-ai/utils/auth gate
 * (D-6), the single place that decides the source by hostname: on localhost it
 * returns the localStorage token (Bearer path); deployed it returns null so the
 * HttpOnly cookie authenticates (the token is never exposed to JS/XSS). Every
 * consumer already attaches Bearer conditionally, so null is handled by omitting
 * the header. Token CAPTURE from the URL + storage stay shell-specific (above).
 */
export const getAuthToken = (): string | null => readAuthCredential();

/**
 * Remove auth token from localStorage
 */
export const removeAuthToken = (): void => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch (error) {
    reportError(error, { event_detail: 'auth_token_remove_failed' });
  }
};

/**
 * Check if auth token exists in localStorage
 * @returns true if token exists, false otherwise
 */
export const hasAuthToken = (): boolean => {
  return getAuthToken() !== null;
};

/**
 * Process auth token from URL and store it
 * This is the main function to call when the app loads
 * @param url - The URL to extract from (usually window.location.search)
 * @returns true if token was found and stored, false otherwise
 */
export const processAuthTokenFromUrl = (url: string): boolean => {
  const token = extractAuthTokenFromUrl(url);

  if (token) {
    storeAuthToken(token);
    return true;
  }

  return false;
};

/**
 * Clean URL by removing the auth bootstrap query params (`authToken`,
 * `userToken` and `tenant`). All are short-lived hand-offs from the main app —
 * once AuthGuard has captured them to localStorage / cookies, leaving them in
 * the URL just risks the JWT showing up in browser history, error-tracking
 * breadcrumbs, and shared screenshots.
 * @returns The cleaned URL without auth bootstrap params
 */
export const cleanAuthTokenFromUrl = (): string => {
  if (typeof window === 'undefined') return '';

  try {
    const url = new URL(window.location.href);
    for (const param of HANDOVER_PARAMS) url.searchParams.delete(param);
    return url.pathname + (url.search || '') + (url.hash || '');
  } catch (error) {
    reportError(error, { event_detail: 'auth_token_url_clean_failed' });
    return window.location.pathname;
  }
};
